import { WorkflowGraphNode } from './flow-graph.model';
import { getWorkflowNodeTypeMeta } from './node-catalog';

/**
 * Utilidades puras de config de nodo, equivalentes a
 * `chatflows/chatflow-builder/models/node-config.util.ts`. Viven aparte de los
 * componentes a propósito: son lógica testeable sin montar Angular.
 */

/** Parsea el config de un nodo (viene como objeto o como string JSON según el endpoint). */
export function parseNodeConfig(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed: unknown = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Normaliza un valor de config a lista de strings no vacíos. */
export function normalizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => !!item);
}

/**
 * `nodeKey` único y legible (`trigger-1`, `action-2`…). Es la identidad del nodo para el
 * backend (`syncNodes` hace match por id o por nodeKey), por eso nunca se renombra una vez
 * creado. Deriva del tipo, no del subtipo, para mantenerlo corto.
 */
export function generateNodeKey(nodeType: string, existingKeys: readonly string[]): string {
  const base = nodeType.toLowerCase().replace(/_/g, '-');
  const used = new Set(existingKeys);
  let index = 1;
  let candidate = `${base}-${index}`;
  while (used.has(candidate)) {
    index += 1;
    candidate = `${base}-${index}`;
  }
  return candidate;
}

/** Título por defecto de un nodo recién creado (si el catálogo no trae label). */
export function defaultNodeTitle(nodeType: string, nodeSubtype: string | null, catalogLabel?: string | null): string {
  if (catalogLabel && catalogLabel.trim()) {
    return catalogLabel.trim();
  }
  return nodeSubtype ?? nodeType;
}

/**
 * Segunda línea de la tarjeta del lienzo: QUÉ hace este paso en concreto.
 *
 * <p>Antes se humanizaba el subtipo crudo (`CREATE_TASK` → "Create task"), así que la tarjeta
 * mostraba el tipo dos veces —el título ya trae la etiqueta del catálogo, en español— y encima
 * la segunda en inglés. Ahora resume la CONFIGURACIÓN: el evento que dispara, el asunto del
 * correo, el título de la tarea, la condición que evalúa… que es lo único que distingue dos
 * pasos del mismo tipo.</p>
 *
 * @param eventLabels etiquetas de eventos del catálogo del backend (`crmEvents`), ya en español.
 *                    Sin ellas se cae a la clave cruda del evento.
 */
export function buildNodeSummary(node: WorkflowGraphNode, eventLabels?: ReadonlyMap<string, string>): string {
  const config = node.config ?? {};
  const texto = (key: string): string => {
    const value = config[key];
    return typeof value === 'string' ? value.trim() : '';
  };

  switch (node.nodeSubtype) {
    case 'CRM_EVENT':
    case 'QUOTE_EVENT':
    case 'MEETING_EVENT':
    case 'CHATFLOW_EVENT': {
      const evento = texto('event');
      if (!evento) {
        return 'Sin evento seleccionado';
      }
      return eventLabels?.get(evento) ?? evento;
    }
    case 'CREATE_TASK':
    case 'SCHEDULE_MEETING':
    case 'CREATE_TICKET':
      return texto('title') || texto('subject') || 'Sin asunto';
    case 'SEND_ONE_TO_ONE_EMAIL':
    case 'SEND_MARKETING_EMAIL':
    case 'SEND_INTERNAL_NOTIFICATION':
      return texto('subject') || texto('message') || 'Sin asunto';
    case 'CHANGE_TICKET_STATUS':
      return resumenTicket(texto('ticketNumber'), texto('statusName'));
    case 'CHANGE_TICKET_PRIORITY':
      return resumenTicket(texto('ticketNumber'), texto('priorityName'));
    case 'CHANGE_TICKET_PIPELINE':
      return resumenTicket(texto('ticketNumber'), texto('pipelineName'));
    case 'ASSIGN_TICKET':
      return texto('ticketNumber') || 'Sin ticket indicado';
    case 'SET_PROPERTY_VALUE':
      return texto('property') ? `${texto('property')} = ${texto('value') || '—'}` : 'Sin propiedad';
    case 'IF_ELSE':
      return resumenCondicion(config['conditions']);
    case 'PROPERTY_CONDITION':
      return [texto('property'), texto('value')].filter(Boolean).join(' · ') || 'Sin condición';
    case 'DELAY':
    case 'WAIT_FOR_TIME':
      return resumenEspera(config);
    default:
      break;
  }

  // Sin regla propia: se aprovecha cualquier texto que el usuario haya escrito antes de caer a
  // la descripción genérica del tipo.
  const generico = texto('title') || texto('subject') || texto('name');
  if (generico) {
    return generico;
  }
  const meta = getWorkflowNodeTypeMeta(node.nodeType);
  return meta ? meta.description : '';
}

function resumenTicket(ticketNumber: string, valor: string): string {
  if (!ticketNumber && !valor) {
    return 'Sin configurar';
  }
  return [ticketNumber || 'ticket sin indicar', valor].filter(Boolean).join(' → ');
}

function resumenCondicion(conditions: unknown): string {
  if (!Array.isArray(conditions) || !conditions.length) {
    return 'Sin condición';
  }
  const primera = (conditions[0] ?? {}) as Record<string, unknown>;
  const property = typeof primera['property'] === 'string' ? primera['property'] : '';
  if (!property) {
    return 'Sin condición';
  }
  const value = primera['value'] == null ? '' : String(primera['value']);
  const resto = conditions.length > 1 ? ` (+${conditions.length - 1})` : '';
  return `${property}${value ? ` = ${value}` : ''}${resto}`;
}

function resumenEspera(config: Record<string, unknown>): string {
  const amount = Number(config['amount']);
  if (!Number.isFinite(amount) || amount <= 0) {
    return 'Sin duración';
  }
  const unidades: Record<string, string> = {
    MINUTES: 'minuto',
    HOURS: 'hora',
    DAYS: 'día',
    WEEKS: 'semana'
  };
  const unidad = unidades[String(config['unit'] ?? 'DAYS')] ?? 'día';
  return `${amount} ${unidad}${amount === 1 ? '' : 's'}`;
}
